import { useCallback, useState } from 'react'
import axios from 'axios'
import { useCancellableRequest } from './useCancellableRequest'
import { SupplierPartField, SupplierPartStatus } from './SupplierPartDrawer'
import { CarahsoftCatalogRow } from './Carahsoft/carahsoftTypes'
import { carahsoftPartFields } from './Carahsoft/carahsoftPartFields'
import { TD_SYNNEX_FIELD_ROWS, TdSynnexSftpItemV2 } from './TdSynnex/tdSynnexTypes'
import { INGRAM_MICRO_FIELD_ROWS, IngramMicroSftpItem } from './IngramMicro/ingramMicroTypes'

export type SidebarSupplier = 'Carahsoft' | 'TD Synnex' | 'Ingram Micro'

export const SIDEBAR_SUPPLIERS: ReadonlySet<string> = new Set<SidebarSupplier>([
  'Carahsoft',
  'TD Synnex',
  'Ingram Micro',
])

type FetchPart = (partNumber: string, mfgPN: string, signal: AbortSignal) => Promise<SupplierPartField[] | null>

const sameId = (a: string | null | undefined, b: string) => a?.trim().toUpperCase() === b.trim().toUpperCase()

const FETCH_PART: Record<SidebarSupplier, FetchPart> = {
  Carahsoft: async (partNumber, mfgPN, signal) => {
    try {
      const res = await axios.get<CarahsoftCatalogRow>('/carahsoft/part', {
        params: { carahsoftPartNumber: partNumber, vendorPartNumber: mfgPN },
        signal,
      })
      return carahsoftPartFields(res.data)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw err
    }
  },
  'TD Synnex': async (partNumber, _mfgPN, signal) => {
    const key = partNumber.trim()
    const res = await axios.post<Record<string, TdSynnexSftpItemV2 | null>>(
      '/tdsynnex/sftp-lookup',
      { mfgPNs: [key] },
      { signal },
    )
    const item = res.data[key]
    if (!item || !sameId(item.synnexPartNo, partNumber)) return null
    return TD_SYNNEX_FIELD_ROWS.map(row => ({ label: row.name, value: row.accessor(item) }))
  },
  'Ingram Micro': async (partNumber, mfgPN, signal) => {
    const key = mfgPN.trim()
    const res = await axios.post<Record<string, IngramMicroSftpItem | null>>(
      '/ingrammicro/sftp-lookup',
      { mfgPNs: [key] },
      { signal },
    )
    const item = res.data[key]
    if (!item || !sameId(item.ingramSKU, partNumber)) return null
    return INGRAM_MICRO_FIELD_ROWS.map(row => ({ label: row.name, value: row.accessor(item) }))
  },
}

export type SupplierPartLookup = {
  supplier: SidebarSupplier
  partNumber: string
  open: boolean
  status: SupplierPartStatus
  fields: SupplierPartField[] | null
}

export function useSupplierPartLookup() {
  const [lookup, setLookup] = useState<SupplierPartLookup | null>(null)
  const { start, cancel } = useCancellableRequest()

  const openPart = useCallback(
    async (supplier: SidebarSupplier, partNumber: string, mfgPN: string) => {
      const { signal, isCurrent } = start()
      const base = { supplier, partNumber, open: true }
      setLookup({ ...base, status: 'loading', fields: null })
      try {
        const fields = await FETCH_PART[supplier](partNumber, mfgPN, signal)
        if (!isCurrent()) return
        setLookup({ ...base, status: fields ? 'found' : 'notFound', fields })
      } catch (err) {
        if (!isCurrent() || axios.isCancel(err)) return
        console.error(`Failed to load ${supplier} part details`, err)
        setLookup({ ...base, status: 'error', fields: null })
      }
    },
    [start],
  )

  const closePart = useCallback(() => {
    cancel()
    setLookup(prev => (prev ? { ...prev, open: false } : prev))
  }, [cancel])

  return { lookup, openPart, closePart }
}
